import { Box } from '@dtx-company/design-system/src/components/Box/Box'
import { Typography } from '@dtx-company/design-system/src'
import { usePrismicAccordionState } from './hooks/usePrismicAccordionState'
import AddIcon from '@mui/icons-material/Add'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import RemoveIcon from '@mui/icons-material/Remove'

export interface PrismicAccordionItem {
  title: string
  description: string
}

export interface PrismicAccordionProps {
  items: PrismicAccordionItem[]
  /** Callback usually for heap tracking of the item of the accordion that was toggle expanded/collapsed */
  onToggledAccordion?: (title: string, expanded: boolean) => void
  /** Whether or not all items in the accordion should start expanded by default */
  shouldItemsStartExpanded?: boolean
}

/**
 * Accordion component to render content that comes from Prismic from the Accordion Slice
 */
export const PrismicAccordion = ({
  items,
  onToggledAccordion,
  shouldItemsStartExpanded = false
}: PrismicAccordionProps): JSX.Element => {
  const { expandedItemsByTitle, toggleExpandItem } = usePrismicAccordionState({
    shouldItemsStartExpanded,
    items
  })

  return (
    <Box>
      {items.map((item, i) => {
        const titleAsText = item.title
        const descAsText = item.description

        if (!titleAsText || !descAsText) return null
        return (
          <MuiAccordion
            disableGutters
            elevation={0}
            square
            expanded={expandedItemsByTitle[titleAsText]}
            onChange={_event => {
              toggleExpandItem(titleAsText)
              onToggledAccordion?.(titleAsText, !expandedItemsByTitle[titleAsText])
            }}
            key={i}
            sx={theme => ({
              maxWidth: '100%',
              backgroundColor: 'transparent',
              borderTop: `1px solid ${theme.builder.primary00}`
            })}
          >
            <MuiAccordionSummary
              data-heap-id={`PrismicAccordion${i}Button`}
              sx={theme => ({
                color: theme.builder.primary00,
                paddingTop: theme.spacing(5),
                paddingRight: 0,
                paddingBottom: theme.spacing(5),
                paddingLeft: 0,

                '& .MuiAccordionSummary-content': {
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  margin: 'unset'
                },
                '& .MuiSvgIcon-root': {
                  marginLeft: theme.spacing(4),
                  fontSize: '1.2rem'
                }
              })}
            >
              <Typography component="div" variant="subtitleMedium">
                {titleAsText}
              </Typography>
              {expandedItemsByTitle[titleAsText] ? <RemoveIcon /> : <AddIcon />}
            </MuiAccordionSummary>
            <MuiAccordionDetails sx={{ overflowY: 'scroll', padding: 0 }}>
              <Box
                sx={{
                  mb: 4.5,
                  '& :first-of-type': {
                    marginTop: 0
                  }
                }}
              >
                <Typography variant="bodyMedium" component="p">
                  {item.description}
                </Typography>
              </Box>
            </MuiAccordionDetails>
          </MuiAccordion>
        )
      })}
    </Box>
  )
}
